const PROJECT_SUB_PATHS = ['dashboard', 'devices', 'catalog', 'members', 'settings'] as const;

/**
 * Pick the destination path when the reader switches the current project.
 *
 * The band's picker goes through this one rule, so choosing a project means
 * the same thing wherever it is chosen:
 *
 * 1. On a page that can be scoped where it stands, stay on that page and
 *    re-point it — a project sub-page keeps its section, and the two lists take
 *    the new `?project=` (the picker's own "All projects" row goes back the
 *    other way). This holds whether or not the list was already filtered: a
 *    bare `/events` is the cross-project listing, so choosing a project there
 *    *narrows the list the reader is reading*. Sending them to that project's
 *    Dashboard instead threw away the page they were on — and with it their
 *    search and status filters — for a project choice they made in the list's
 *    own header.
 * 2. Everywhere else — `/projects`, `/dashboard`, a record, `/account` — choosing
 *    a project means going to it, so it lands on that project's Dashboard.
 */
export function resolveProjectSwitchPath(
  pathname: string,
  search: string,
  newProjectId: string,
): string {
  const sub = pathname.match(/^\/projects\/[^/]+\/([^/]+)/)?.[1];
  if (sub && (PROJECT_SUB_PATHS as readonly string[]).includes(sub)) {
    return `/projects/${newProjectId}/${sub}`;
  }

  // The two lists are the pages that can be re-scoped where they stand, so
  // choosing a project on one *is* the scope change: the list stays and the
  // filter follows.
  if (pathname === '/events' || pathname === '/cases') {
    const params = new URLSearchParams(search);
    params.set('project', newProjectId);
    return `${pathname}?${params.toString()}`;
  }

  return `/projects/${newProjectId}/dashboard`;
}

/**
 * The same move in the other direction: from a project-scoped list back to the
 * cross-project one, which is the picker's "All projects" row.
 *
 * `?project=` comes off and everything else the reader put in the URL — the
 * search, the status filter — stays: widening the list is not a reason to lose
 * what they typed. Only the two lists offer this; on a project sub-page there
 * is no cross-project listing to go to.
 */
export function resolveAllProjectsPath(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete('project');
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
